import React, { useEffect, useState } from 'react';
import {
  Box,
  TextField,
  MenuItem,
  Button,
  Typography,
  FormControlLabel,
  Checkbox,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

const Prediction = ({ authHeaders, backEnd }) => {
  const theme = useTheme();
  const [data, setData] = useState({});
  const [formData, setFormData] = useState({
    град: '',
    квартал: '',
    площ: '',
    стаи: '',
    етаж: '',
    общо_етажи: '',
    тухла: 0,
    в_строеж: 0,
    асансьор: 0,
    обзаведен: 0,
    видео_наблюдение: 0,
    контролиран_достъп: 0,
    саниран: 0,
    интернет: 0,
    гараж: 0,
    ново_строителство: 0,
  });
  const [price, setPrice] = useState(null);
  const [loading, setLoading] = useState(true); // Loading state
  const [avgPrices, setAvgPrices] = useState({ cityAvg: null, locationAvg: null }); // New state
  const [openDialog, setOpenDialog] = useState(false); // Dialog state

  function groupCityAndRegionWithRenamedKeys(data) {
  const grouped = [];
  const seen = new Set();
  const allKeys = Object.keys(data);

  allKeys.forEach(key => {
    if (seen.has(key)) return;

    if (!key.startsWith("област ")) {
      const regionKey = "област " + key;

      if (data[regionKey]) {
        grouped.push([key, data[key]]);
        grouped.push([`${key} област`, data[regionKey]]);
        seen.add(key);
        seen.add(regionKey);
      } else {
        grouped.push([key, data[key]]);
        seen.add(key);
      }
    }
  });

  allKeys.forEach(key => {
    if (key.startsWith("област ") && !seen.has(key)) {
      const cityName = key.replace("област ", "");
      grouped.push([`${cityName} област`, data[key]]);
      seen.add(key);
    }
  });

  return Object.fromEntries(grouped);
}

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const response = await fetch(backEnd, {
          method: 'GET',
          headers: {
            Authorization: authHeaders,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch locations');
        }

        const responseData = await response.json();
        const final = groupCityAndRegionWithRenamedKeys(responseData)
        setData(final);
      } catch (error) {
        console.error('Error fetching locations:', error);
      } finally {
        setLoading(false); // Set loading to false when data is fetched
      }
    };

    fetchLocations();
  }, [authHeaders, backEnd]);

  // Fetch average prices for city and location
  const fetchAveragePrices = async (city, location) => {
    try {
      const response = await fetch(backEnd + 'get_avg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: authHeaders,
        },
        body: JSON.stringify({ city, location }),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch average prices');
      }

      const result = await response.json();
      setAvgPrices({
        cityAvg: Math.floor(result[0]),      // Average price for the city
        locationAvg: Math.floor(result[1]),  // Average price for the location
      });
    } catch (error) {
      console.error('Error fetching average prices:', error);
      setAvgPrices({ cityAvg: 'N/A', locationAvg: 'N/A' });
    }
  };


  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: checked ? 1 : 0,
    }));
  };

  const handleSubmit = async () => {
    try {
      const response = await fetch(backEnd + 'get_price', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: authHeaders,
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error(response.error) 
      }

      const result = await response.json();
      if (result.error.includes("Not enough credits")) {
        setOpenDialog(true); // Open the dialog when credits are insufficient
        return;
      }
      setPrice(result.price);
      await fetchAveragePrices(formData.град, formData.квартал);
    } catch (error) {
      console.error('Error getting price:', error);
      alert('An error occurred while calculating the price.');
    }
  };

  return (
    <Grid container spacing={2} sx={{ padding: 3 }}>
      {/* Form Section */}
      <Grid item xs={12} md={6}>
        <Box sx={{ maxWidth: 600, margin: '0 auto', textAlign: 'left' }}>

          {loading ? ( // Show loading indicator
            <Typography variant="h6" color="textSecondary">
              Зареждане...
            </Typography>
          ) : (
            <>
            <TextField
  select
  label="Имот"
  value="Апартамент"
  name="imot"
 color="#1CA6AF"
 fullWidth

>
  <MenuItem value="Апартамент">Апартамент</MenuItem>
</TextField>
              {/* City Dropdown */}
              <TextField
                select
                label="Град"
                name="град"
                value={formData.град}
                onChange={(e) => {
                  handleChange(e);
                  setFormData((prev) => ({
                    ...prev,
                    квартал: '', // Reset location when city changes
                  }));
                }}
                fullWidth
                margin="normal"
                color="#1CA6AF"
              >
                {Object.keys(data).length > 0 ? (
                  Object.keys(data).map((град) => (
                    <MenuItem key={град} value={град}>
                      {град}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled>Информацията не е налична</MenuItem>
                )}
              </TextField>

              {/* Location Dropdown */}
              <TextField
                select
                label="Квартал"
                name="квартал"
                value={formData.квартал}
                onChange={handleChange}
                fullWidth
                margin="normal"
                disabled={!formData.град}
                color="#1CA6AF"
              >
                {formData.град && data[formData.град]?.length > 0 ? (
                  data[formData.град].map((квартал) => (
                    <MenuItem key={квартал} value={квартал}>
                      {квартал}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled>Информацията не е налична</MenuItem>
                )}
              </TextField>

              {/* Numeric Fields */}
              <TextField
                label="Площ (кв. м.)"
                name="площ"
                value={formData.площ}
                onChange={handleChange}
                fullWidth
                margin="normal"
                type="number"
                color="#1CA6AF"
              />
              <TextField
                label="Стаи (брой)"
                name="стаи"
                value={formData.стаи}
                onChange={handleChange}
                fullWidth
                margin="normal"
                type="number"
                color="#1CA6AF"
              />
              <TextField
                label="Етаж"
                name="етаж"
                value={formData.етаж}
                onChange={handleChange}
                fullWidth
                margin="normal"
                type="number"
                color="#1CA6AF"
              />
              <TextField
                label="Общо етаж"
                name="общо_етажи"
                value={formData.общо_етажи}
                onChange={handleChange}
                fullWidth
                margin="normal"
                type="number"
                color="#1CA6AF"
              />

              {/* Checkboxes */}
              <Box
  sx={{
    display: 'grid',
    gridTemplateColumns: '1fr 1fr', // Two equal columns
    gap: 1, // Optional spacing between items
          rowGap: 1, // 8px
columnGap: 3, // 24px
  }}
>
  {[
    
    'в_строеж',
    'обзаведен',
    'асансьор',
    'видео_наблюдение',
    'контролиран_достъп',
    'саниран',
    'интернет',
    'ново_строителство (след 2010г.)',
  ].map((field) => (
    <FormControlLabel
      key={field}
      control={
        <Checkbox
          name={field}
          checked={formData[field] === 1}
          onChange={handleCheckboxChange}
          color="#1CA6AF"
        />
      }
      label={field.replace(/_/g, ' ').toUpperCase()}
      sx={{
    fontSize: '12px', // Set your desired font size
    '& .MuiFormControlLabel-label': {
      fontSize: '12px', // Target the label specifically

    },
  }}
    />
  ))}
</Box>

              {/* Submit Button */}
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                sx={{ marginTop: 2 }}
              >
                Оцени
              </Button>
            </>
          )}
        </Box>
      </Grid>

      {/* Price Display Section */}
      <Grid item xs={12} md={6}>
        <Box
          sx={{
            maxWidth: 600,
            margin: '0 auto',
            textAlign: 'center',
            border: '1px solid #ddd',
            borderRadius: 2,
            padding: 3,
          }}
        >
          <Typography
            variant="h4"
            gutterBottom
            sx={{
              color: theme.palette.text.primary,
            }}
          >
            Изчислена прогнозна оценка
          </Typography>
          {price !== null ? (
            <>
            <Typography
              variant="h5"
              sx={{
                color: theme.palette.text.primary,
              }}
            >
              {price}€
            </Typography>
            <Typography variant="h6" sx={{ marginTop: 2 }}>
                Средна цена за град: {avgPrices.cityAvg !== null ? `${avgPrices.cityAvg}€` : 'Loading...'}
              </Typography>
              <Typography variant="h6">
                Средна цена за квартала: {avgPrices.locationAvg !== null ? `${avgPrices.locationAvg}€` : 'Loading...'}
              </Typography>
            </>
            
          ) : (
            <Typography variant="h6" color="textSecondary">
              Оценката не е направена
            </Typography>
          )}
        </Box>
      </Grid>
       {/* Insufficient Credits Dialog */}
       <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>Недостатъчен баланс</DialogTitle>
        <DialogContent>
          <Typography>Моля, добавете още обяви, за да продължите.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} color="textSecondary">
            Затвори
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default Prediction;
